import {
  Box,
  MaterialTableWrapper,
  MaterialTableWrapperProps,
  ModalContainer,
  MRT_ColumnDef,
  MRT_TableOptions,
  RowActionButton,
  useDisclosure,
  withThemeProvider,
} from '@ouster/webviz-react-components';

import { useMemo, useState } from 'react';
import { BiSolidCog, BiDetail } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';

import AdminStorageConfig from './AdminStorageConfig';
import { AdminDashboardOrgBrief } from './types';
import { useAdminDashboardSummary } from './useAdminDashboard';
import { formatBytes } from '../../utils/misc';

const ThemedMaterialTableWrapper =
  withThemeProvider<MaterialTableWrapperProps<AdminDashboardOrgBrief>>(
    MaterialTableWrapper
  );

export default function AdminOverallOrgsSummary() {
  const {
    data: summaryData,
    isLoading: summaryLoading,
    isRefetching,
  } = useAdminDashboardSummary();

  const navigate = useNavigate();

  const columns = useMemo<MRT_ColumnDef<AdminDashboardOrgBrief>[]>(
    () => [
      {
        accessorKey: 'organization_name',
        header: 'Organization',
        minSize: 300,
      },
      {
        accessorKey: 'status',
        header: 'Status',
      },
      {
        accessorKey: 'created_at',
        header: 'Created at',
        Cell: ({ row }) => {
          const createdAt = row.original.created_at;
          return typeof createdAt === 'string'
            ? new Date(createdAt).toISOString().split('T')[0]
            : 'Invalid Date';
        },
      },
      {
        accessorKey: 'total_users',
        header: 'Users',
      },
      {
        accessorKey: 'space_used',
        header: 'Space usage',
        Cell: ({ row }) => formatBytes(row.original.space_used),
      },
      {
        accessorKey: 'allowed_space',
        header: 'Storage limit',
        Cell: ({ row }) => {
          if (row.original.allowed_space === -1) {
            return 'Unlimited';
          }
          return formatBytes(row.original.allowed_space);
        },
      },
      {
        accessorKey: 'upload_limit',
        header: 'Upload limit',
        Cell: ({ row }) => {
          if (row.original.upload_limit === -1) {
            return 'Unlimited';
          }
          return formatBytes(row.original.upload_limit);
        },
      },
    ],
    []
  );

  const {
    isOpen: isConfigDialogOpen,
    onOpen: onConfigDialogOpen,
    onClose: onConfigDialogClose,
  } = useDisclosure();

  const [configOrgId, setConfigOrgId] = useState<number | undefined>(undefined);

  const storageConfigHandler = (row: AdminDashboardOrgBrief) => {
    setConfigOrgId(row.organization_id);
    onConfigDialogOpen();
  };
  const configOrgInfo = summaryData?.organizations.find(
    (org) => org.organization_id === configOrgId
  );

  const organizationExploreHandler = (row: AdminDashboardOrgBrief) => {
    navigate(`/admin_dashboard/org/${row.organization_id}`);
  };

  const tableOptions: MRT_TableOptions<AdminDashboardOrgBrief> = {
    columns,
    data: summaryData?.organizations || [],
    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
      columnPinning: { right: ['mrt-row-actions'], left: ['name'] },
      showGlobalFilter: true,
    },
    enableRowActions: true,
    positionGlobalFilter: 'left',
    enableSorting: true,
    enableToolbarInternalActions: false,
    positionActionsColumn: 'last',
    muiTableContainerProps: {
      sx: {
        maxHeight: `calc(100% - 120px)`,
      },
    },
    muiPaginationProps: {
      rowsPerPageOptions: [50, 100, 200, 300],
    },
    getRowId: (originalRow) =>
      `${originalRow.organization_id} - ${originalRow.organization_name}`,
    renderRowActions: ({ row }) => (
      <Box flexWrap="nowrap" display="flex">
        <RowActionButton<AdminDashboardOrgBrief>
          title="Configure Storage"
          icon={<BiSolidCog />}
          row={row.original}
          onClickHandler={storageConfigHandler}
          isDisabled={row.original.organization_id === 1}
        />
        <RowActionButton<AdminDashboardOrgBrief>
          title="Explore Company Details"
          icon={<BiDetail />}
          row={row.original}
          onClickHandler={organizationExploreHandler}
        />
      </Box>
    ),
    state: {
      isLoading: summaryLoading,
      showLoadingOverlay: false,
      showAlertBanner: false,
      showSkeletons: summaryLoading || isRefetching,
    },
  };

  return (
    <Box
      display="grid"
      gap="2rem"
      p="1.5rem"
      border="1px solid var(--color-unify-white-40)"
      borderRadius={8}
      height="100%"
      overflow="hidden"
      data-testid="admin-overall-orgs-box"
    >
      <ThemedMaterialTableWrapper {...tableOptions} />

      {configOrgInfo && (
        <ModalContainer
          isOpen={isConfigDialogOpen}
          onClose={onConfigDialogClose}
          header={configOrgInfo.organization_name}
          content={
            <AdminStorageConfig
              {...configOrgInfo}
              handleClose={onConfigDialogClose}
            />
          }
          footer={null}
        />
      )}
    </Box>
  );
}
